import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { 
  onAuthStateChanged, 
  signOut as firebaseSignOut
} from 'firebase/auth';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';

// Tiempo de inactividad antes de cerrar sesión (en milisegundos)
// 30 minutos = 30 * 60 * 1000 = 1,800,000 ms
const INACTIVITY_TIMEOUT = 30 * 60 * 1000;

// Crear el contexto
const AuthContext = createContext();

// Hook personalizado para usar el contexto
export const useAuth = () => {
  return useContext(AuthContext);
};

// Proveedor del contexto
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimerRef = useRef(null);
  const navigate = useNavigate();

  // Función para cerrar sesión con recarga forzada
  const signOut = () => {
    return firebaseSignOut(auth).then(() => {
      // Forzar recarga completa de la página al cerrar sesión
      window.location.reload();
    });
  };

  // Función para reiniciar el temporizador de inactividad (usando useCallback)
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    // Solo configurar el temporizador si hay un usuario autenticado
    if (currentUser) {
      inactivityTimerRef.current = setTimeout(() => {
        // Cerrar sesión por inactividad
        console.log('Cerrando sesión por inactividad');
        signOut();
        navigate('/login');
      }, INACTIVITY_TIMEOUT);
    }
  }, [currentUser, navigate]);

  // Efecto para configurar el evento de actividad del usuario
  useEffect(() => {
    // Lista de eventos que consideramos como "actividad"
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart'
    ];

    // Callback para reset del temporizador cuando hay actividad
    const handleUserActivity = () => {
      resetInactivityTimer();
    };

    // Agregar event listeners para todos los eventos de actividad
    activityEvents.forEach(eventName => {
      document.addEventListener(eventName, handleUserActivity);
    });

    // Iniciar temporizador
    resetInactivityTimer();

    // Limpiar event listeners y temporizador al desmontar
    return () => {
      activityEvents.forEach(eventName => {
        document.removeEventListener(eventName, handleUserActivity);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [currentUser, resetInactivityTimer]); // Ahora incluye resetInactivityTimer

  // Efecto para la autenticación
  useEffect(() => {
    // Suscripción al estado de autenticación
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
      // Reiniciar timer cuando cambia el estado de autenticación
      resetInactivityTimer();
    });

    // Limpiar la suscripción al desmontar
    return unsubscribe;
  }, [resetInactivityTimer]); // Ahora incluye resetInactivityTimer

  // Valor que se compartirá en el contexto
  const value = {
    currentUser,
    signOut,
    resetInactivityTimer
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};